import React, { useState } from 'react';
import './RequestQuote.css';

function RequestQuote() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [stay, setStay] = useState('');
  const [charter, setCharter] = useState('');
  const [marketing, setMarketing] = useState('');
  const [terms, setTerms] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData(form);

      formData.set('stay', stay || 'No preference selected');
      formData.set('charter', charter || 'No charter preference selected');
      formData.set('marketing', marketing === 'Yes' ? 'Yes' : 'No');
      formData.set('terms', terms === 'Accepted' ? 'Accepted' : 'Not accepted');

      formData.set('access_key', process.env.REACT_APP_ACCESS_KEY);
      formData.set('subject', 'New Quote Request – Chasylm Tours & Safaris');
      formData.set('from_name', formData.get('name') || 'Quote Request');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Quote form submission error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="quote-page">
        <main className="quote-wrap">
          <div className="quote-card quote-success">
            <span className="quote-badge">Enquiry received</span>
            <h2>Thank you — your trip starts here.</h2>
            <p>
              Chasylm can now review your requirements and prepare a tailored
              quote.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="quote-page">
      <header className="quote-hero">
        <div className="quote-hero-inner">
          <div className="quote-brand">Chasylm Tours &amp; Safaris</div>
          <h1>Tell us where you want to go.</h1>
          <p>
            Request a personalized safari, holiday or tour quote. No fixed
            package required — our team will build the trip around your dates,
            interests and travel style.
          </p>

          <div className="quote-steps">
            <span className="quote-step active">01 Your details</span>
            <span className="quote-step">02 Trip details</span>
            <span className="quote-step">03 Preferences</span>
            <span className="quote-step">04 Submit</span>
          </div>
        </div>
      </header>

      <main className="quote-wrap">
        <form className="quote-card" onSubmit={handleSubmit} noValidate>
          {/* 01 · Contact */}
          <section className="quote-section">
            <div className="quote-section-head">
              <div>
                <div className="quote-eyebrow">01 · Contact</div>
                <h2>Who are we planning for?</h2>
                <p className="quote-hint">
                  Use the details of the person responsible for the booking.
                </p>
              </div>
              <span className="quote-badge">Secure enquiry</span>
            </div>

            <div className="quote-fields">
              <div className="quote-field">
                <label htmlFor="name">
                  Full name <span className="required">*</span>
                </label>
                <p className="quote-description">
                  The full name of the person making the booking.
                </p>
                <input
                  id="name"
                  name="name"
                  required
                  autoComplete="name"
                  placeholder="e.g. John Doe"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="email">
                  Email address <span className="required">*</span>
                </label>
                <p className="quote-description">
                  We’ll send your quote and any follow-up messages here.
                </p>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="e.g. johndoe@example.com"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="phone">
                  Phone / WhatsApp number <span className="required">*</span>
                </label>
                <p className="quote-description">
                  Include country code so we can reach you easily.
                </p>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  autoComplete="tel"
                  placeholder="e.g. +254 712 345 678"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="residence">
                  Country of residence <span className="required">*</span>
                </label>
                <p className="quote-description">Where you currently live.</p>
                <input
                  id="residence"
                  name="residence"
                  required
                  placeholder="e.g. Kenya, United States, United Kingdom"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="travellerType">
                  Traveller type <span className="required">*</span>
                </label>
                <p className="quote-description">
                  Helps us apply the correct rates and park fees.
                </p>
                <select id="travellerType" name="travellerType" required>
                  <option value="">Select traveller type</option>
                  <option>Kenya resident</option>
                  <option>International / non-resident</option>
                  <option>Resident of another African country</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="nationality">
                  Nationality <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">Useful for visa and entry guidance.</p>
                <input
                  id="nationality"
                  name="nationality"
                  placeholder="e.g. Kenyan, American, British"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="contact">Preferred contact method</label>
                <p className="quote-description">How would you like us to get in touch?</p>
                <select id="contact" name="contact">
                  <option value="">Select preferred contact method</option>
                  <option>Email</option>
                  <option>WhatsApp</option>
                  <option>Phone call</option>
                </select>
              </div>
            </div>
          </section>

          {/* 02 · Trip */}
          <section className="quote-section">
            <div className="quote-section-head">
              <div>
                <div className="quote-eyebrow">02 · Trip</div>
                <h2>What are you planning?</h2>
                <p className="quote-hint">
                  Give us enough detail to prepare a useful first quote.
                </p>
              </div>
            </div>

            <div className="quote-fields">
              <div className="quote-field">
                <label htmlFor="tripType">
                  Trip type <span className="required">*</span>
                </label>
                <p className="quote-description">Choose the main style of trip.</p>
                <select id="tripType" name="tripType" required>
                  <option value="">Select trip type</option>
                  <option>Safari</option>
                  <option>Beach holiday</option>
                  <option>City &amp; culture</option>
                  <option>Adventure / outdoors</option>
                  <option>Honeymoon / romantic getaway</option>
                  <option>Family holiday</option>
                  <option>Business / corporate travel</option>
                  <option>Group tour</option>
                  <option>Custom / other</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="destination">
                  Destination(s) <span className="required">*</span>
                </label>
                <p className="quote-description">List the main places you want to visit.</p>
                <input
                  id="destination"
                  name="destination"
                  required
                  placeholder="e.g. Maasai Mara, Diani Beach, Lamu, Amboseli"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="travellers">
                  Total number of travellers <span className="required">*</span>
                </label>
                <p className="quote-description">Include adults and children.</p>
                <input
                  id="travellers"
                  name="travellers"
                  type="number"
                  min="1"
                  required
                  placeholder="e.g. 4"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="adults">
                  Number of adults <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">Adults aged 18 and over.</p>
                <input
                  id="adults"
                  name="adults"
                  type="number"
                  min="0"
                  placeholder="e.g. 2"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="children">
                  Number of children <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">Children under 18 years.</p>
                <input
                  id="children"
                  name="children"
                  type="number"
                  min="0"
                  placeholder="e.g. 2"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="childAges">
                  Ages of children <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">
                  Helps us choose suitable activities and rooming.
                </p>
                <input
                  id="childAges"
                  name="childAges"
                  placeholder="e.g. 6, 10, 14"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="startDate">
                  Preferred start date <span className="required">*</span>
                </label>
                <p className="quote-description">
                  The date you would like the trip to begin.
                </p>
                <input id="startDate" name="startDate" type="date" required />
              </div>

              <div className="quote-field">
                <label htmlFor="endDate">
                  Preferred end date <span className="required">*</span>
                </label>
                <p className="quote-description">
                  The date you would like the trip to finish.
                </p>
                <input id="endDate" name="endDate" type="date" required />
              </div>

              <div className="quote-field">
                <label htmlFor="flexible">Are your dates flexible?</label>
                <p className="quote-description">
                  Flexible dates often give better availability and rates.
                </p>
                <select id="flexible" name="flexible">
                  <option value="">Select flexibility</option>
                  <option>No – fixed dates</option>
                  <option>Yes — up to 3 days</option>
                  <option>Yes — up to 1 week</option>
                  <option>Very flexible</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="arrival">
                  Arrival airport or city <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">Where you will arrive in Kenya.</p>
                <input
                  id="arrival"
                  name="arrival"
                  placeholder="e.g. JKIA (Nairobi), Mombasa, Kisumu"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="departure">
                  Departure airport or city <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">Where you plan to leave from.</p>
                <input
                  id="departure"
                  name="departure"
                  placeholder="e.g. Wilson Airport, JKIA, Diani"
                />
              </div>

              <div className="quote-field">
                <label htmlFor="airportTransfer">Do you need airport transfers?</label>
                <p className="quote-description">
                  We can arrange private transfers if required.
                </p>
                <select id="airportTransfer" name="airportTransfer">
                  <option value="">Select option</option>
                  <option>Yes</option>
                  <option>No</option>
                  <option>Not sure yet</option>
                </select>
              </div>
            </div>
          </section>

          {/* 03 · Preferences */}
          <section className="quote-section">
            <div className="quote-section-head">
              <div>
                <div className="quote-eyebrow">03 · Preferences</div>
                <h2>Shape the experience</h2>
                <p className="quote-hint">
                  These help us match the itinerary and suppliers to your needs.
                </p>
              </div>
            </div>

            <div className="quote-fields">
              <div className="quote-field">
                <label htmlFor="stay">Preferred accommodation style</label>
                <p className="quote-description">
                  Choose the level of comfort that suits your budget and style.
                </p>
                <select
                  id="stay"
                  name="stayPreference"
                  value={stay}
                  onChange={(e) => setStay(e.target.value)}
                >
                  <option value="">Select accommodation style</option>
                  <option value="Budget">Budget / value</option>
                  <option value="Mid-range">Comfort / mid-range</option>
                  <option value="Luxury">Luxury / premium</option>
                  <option value="Mixed">Mix of styles</option>
                  <option value="No preference">No preference</option>
                </select>
              </div>

              <div className="quote-field quote-field--full">
                <label htmlFor="charter">
                  Would you like us to request a charter flight quote?
                </label>
                <p className="quote-description">
                  Charter flights can save time on longer routes.
                </p>
                <select
                  id="charter"
                  name="charterPreference"
                  value={charter}
                  onChange={(e) => setCharter(e.target.value)}
                >
                  <option value="">Select option</option>
                  <option value="No">No, road or scheduled air is fine</option>
                  <option value="Yes">Yes, request a charter quote</option>
                  <option value="Not sure">Not sure — please advise me</option>
                </select>

                <div className="quote-notice">
                  Charter requests are subject to availability and applicable
                  aviation requirements. Chasylm will request availability and
                  pricing from an appropriate licensed aviation partner;
                  submitting this request does not confirm or book a charter
                  flight.
                </div>
              </div>

              <div className="quote-field">
                <label htmlFor="charterDestination">
                  Preferred charter route or destination{' '}
                  <span className="optional">(if requesting charter)</span>
                </label>
                <p className="quote-description">
                  Select the most relevant route if you would like a charter quote.
                </p>
                <select id="charterDestination" name="charterDestination">
                  <option value="">Select route (if applicable)</option>
                  <option>Wilson Airport → Maasai Mara</option>
                  <option>Wilson Airport → Lamu</option>
                  <option>Wilson Airport → Diani</option>
                  <option>JKIA → Maasai Mara</option>
                  <option>JKIA → Lamu</option>
                  <option>JKIA → Diani</option>
                  <option>Maasai Mara → Wilson Airport</option>
                  <option>Lamu → Wilson Airport</option>
                  <option>Diani → Wilson Airport</option>
                  <option>Other route — describe in notes below</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="rooms">Room preference</label>
                <p className="quote-description">
                  Tell us how you would like the group to be roomed.
                </p>
                <select id="rooms" name="rooms">
                  <option value="">Select room preference</option>
                  <option>Not sure yet</option>
                  <option>Single</option>
                  <option>Double / twin</option>
                  <option>Family room</option>
                  <option>Multiple rooms</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="transport">Preferred transport style</label>
                <p className="quote-description">
                  This helps us design the most comfortable itinerary.
                </p>
                <select id="transport" name="transport">
                  <option value="">Select transport preference</option>
                  <option>4x4 safari vehicle</option>
                  <option>Private vehicle</option>
                  <option>Shared vehicle</option>
                  <option>Domestic flights</option>
                  <option>Mix of road &amp; air</option>
                  <option>Not sure</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="budget">Estimated budget per person (USD)</label>
                <p className="quote-description">
                  Approximate budget helps us suggest suitable options.
                </p>
                <select id="budget" name="budget">
                  <option value="">Select budget range</option>
                  <option>Prefer to discuss</option>
                  <option>Under USD 500</option>
                  <option>USD 500–1,000</option>
                  <option>USD 1,000–2,500</option>
                  <option>USD 2,500–5,000</option>
                  <option>USD 5,000+</option>
                </select>
              </div>

              <div className="quote-field quote-field--full">
                <label htmlFor="interests">
                  Interests and activities <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">
                  Tell us what you most enjoy so we can tailor the experience.
                </p>
                <textarea
                  id="interests"
                  name="interests"
                  rows={4}
                  placeholder="e.g. Wildlife viewing, beaches, hiking, photography, cultural experiences, food, boat trips, conservation..."
                />
              </div>

              <div className="quote-field quote-field--full">
                <label htmlFor="requirements">
                  Dietary, accessibility or other special requirements{' '}
                  <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">
                  Any needs we should consider when planning the trip.
                </p>
                <textarea
                  id="requirements"
                  name="requirements"
                  rows={4}
                  placeholder="e.g. Vegetarian meals, wheelchair access, allergy information..."
                />
              </div>

              <div className="quote-field quote-field--full">
                <label htmlFor="notes">
                  Anything else we should know?{' '}
                  <span className="optional">(optional)</span>
                </label>
                <p className="quote-description">
                  Special occasions, must-see places, previous experience, etc.
                </p>
                <textarea
                  id="notes"
                  name="notes"
                  rows={4}
                  placeholder="e.g. Special occasion, preferred pace, must-see places..."
                />
              </div>
            </div>
          </section>

          {/* 04 · International travel */}
          <section className="quote-section">
            <div className="quote-section-head">
              <div>
                <div className="quote-eyebrow">04 · International travel</div>
                <h2>For travellers coming to Kenya</h2>
                <p className="quote-hint">
                  These questions help us identify support you may need. Passport
                  details are not required for a quote.
                </p>
              </div>
            </div>

            <div className="quote-fields">
              <div className="quote-field">
                <label htmlFor="flights">
                  Have you already arranged international flights?
                </label>
                <p className="quote-description">
                  This helps us plan airport transfers and start times.
                </p>
                <select id="flights" name="flights">
                  <option value="">Select option</option>
                  <option>Not yet</option>
                  <option>Yes</option>
                  <option>I'll need help arranging them</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="visa">Do you need visa or entry assistance?</label>
                <p className="quote-description">
                  We can provide guidance based on your nationality.
                </p>
                <select id="visa" name="visa">
                  <option value="">Select option</option>
                  <option>Not sure</option>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>

              <div className="quote-field">
                <label htmlFor="insurance">Travel insurance status</label>
                <p className="quote-description">
                  Travel insurance is strongly recommended for all trips.
                </p>
                <select id="insurance" name="insurance">
                  <option value="">Select option</option>
                  <option>I'll arrange it myself</option>
                  <option>I need guidance</option>
                  <option>Not sure yet</option>
                </select>
              </div>

              <div className="quote-field quote-field--full">
                <div className="quote-notice">
                  <strong>Privacy note:</strong> Please do not enter passport
                  numbers, bank details, medical records or other highly sensitive
                  information in this quote form.
                </div>
              </div>
            </div>
          </section>

          {/* 05 · Communication */}
          <section className="quote-section">
            <div className="quote-section-head">
              <div>
                <div className="quote-eyebrow">05 · Communication</div>
                <h2>Stay in touch</h2>
                <p className="quote-hint">
                  Your marketing choice is separate from your quote request.
                </p>
              </div>
            </div>

            <div className="quote-fields">
              <div className="quote-field quote-field--full">
                <label htmlFor="marketing">Marketing updates</label>
                <p className="quote-description">
                  Optional. You can unsubscribe at any time.
                </p>
                <select
                  id="marketing"
                  name="marketingCheckbox"
                  value={marketing}
                  onChange={(e) => setMarketing(e.target.value)}
                >
                  <option value="">Select preference</option>
                  <option value="Yes">
                    Yes, I'd like to receive Chasylm travel inspiration, special offers and occasional tour updates
                  </option>
                  <option value="No">No, thank you</option>
                </select>
              </div>

              <div className="quote-field quote-field--full">
                <label htmlFor="terms">
                  Terms confirmation <span className="required">*</span>
                </label>
                <p className="quote-description">
                  Required before submitting your quote request.
                </p>
                <select
                  id="terms"
                  name="termsCheckbox"
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  required
                >
                  <option value="">Please confirm</option>
                  <option value="Accepted">
                    I confirm that the information provided is accurate and I agree that Chasylm Tours may contact me regarding this quote request
                  </option>
                </select>
              </div>
            </div>
          </section>

          {error && (
            <div className="quote-notice quote-error" role="alert">
              {error}
            </div>
          )}

          <div className="quote-form-footer">
            {/* <div className="quote-privacy">
              After submission, the team should receive the enquiry in the
              CRM/email inbox with a unique quote-request ID.
            </div> */}

            <button className="quote-btn" type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Request My Quote →'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default RequestQuote;